import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

interface UserProfile {
  first_name: string | null;
  last_name: string | null;
  phone_verified: boolean | null;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  userRole: 'admin' | 'technician' | null;
  isAdmin: boolean;
  userProfile: UserProfile | null;
  signOut: () => Promise<void>;
  refreshUserRole: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [userRole, setUserRole] = useState<'admin' | 'technician' | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);

  const fetchUserProfile = async (
    userId: string, 
    passedSession?: Session | null, 
    retryCount = 0
  ): Promise<void> => {
    try {
      // Use passed session first, fall back to getSession() for refresh scenarios
      let currentSession = passedSession;
      if (!currentSession) {
        const { data: { session } } = await supabase.auth.getSession();
        currentSession = session;
      }
      
      if (!currentSession) {
        if (retryCount < 3) {
          await new Promise(resolve => setTimeout(resolve, 200 * (retryCount + 1)));
          return fetchUserProfile(userId, null, retryCount + 1);
        }
        console.warn('No session available after retries, skipping profile fetch');
        return;
      }

      const { data, error } = await supabase
        .from('profiles')
        .select('first_name, last_name, phone_verified')
        .eq('id', userId)
        .maybeSingle();

      if (error) throw error;
      setUserProfile(data);
    } catch (error) {
      console.error('Error fetching user profile:', error);
      if (retryCount < 3) {
        await new Promise(resolve => setTimeout(resolve, 200 * (retryCount + 1)));
        return fetchUserProfile(userId, passedSession, retryCount + 1);
      }
      setUserProfile(null);
    }
  };

  const fetchUserRole = async (
    userId: string, 
    passedSession?: Session | null, 
    retryCount = 0
  ): Promise<void> => {
    try {
      // Use passed session first, fall back to getSession() for refresh scenarios
      let currentSession = passedSession;
      if (!currentSession) {
        const { data: { session } } = await supabase.auth.getSession();
        currentSession = session;
      }
      
      if (!currentSession) {
        if (retryCount < 3) {
          await new Promise(resolve => setTimeout(resolve, 200 * (retryCount + 1)));
          return fetchUserRole(userId, null, retryCount + 1);
        }
        console.warn('No session available after retries, defaulting to technician');
        setUserRole('technician');
        return;
      }

      const { data, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', userId)
        .order('role', { ascending: true }); // admin comes before technician

      if (error) throw error;

      // If user has admin role, set as admin (they can also be technician)
      const hasAdmin = data?.some(r => r.role === 'admin');
      setUserRole(hasAdmin ? 'admin' : 'technician');
    } catch (error) {
      console.error('Error fetching user role:', error);
      if (retryCount < 3) {
        await new Promise(resolve => setTimeout(resolve, 200 * (retryCount + 1)));
        return fetchUserRole(userId, passedSession, retryCount + 1);
      }
      setUserRole('technician'); // Default to technician
    }
  };

  const refreshUserRole = async () => {
    if (user) {
      await fetchUserRole(user.id);
    }
  };

  useEffect(() => {
    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, currentSession) => {
        setSession(currentSession);
        setUser(currentSession?.user ?? null);
        
        if (currentSession?.user) {
          // Pass the session directly - don't rely on cookie storage timing
          setTimeout(() => {
            fetchUserProfile(currentSession.user.id, currentSession);
            fetchUserRole(currentSession.user.id, currentSession);
          }, 0);
        } else {
          setUserRole(null);
          setUserProfile(null);
        }
        
        setLoading(false);
      }
    );

    // Check for existing session
    supabase.auth.getSession().then(({ data: { session: currentSession } }) => {
      setSession(currentSession);
      setUser(currentSession?.user ?? null);
      
      if (currentSession?.user) {
        // Pass the session directly
        setTimeout(() => {
          fetchUserProfile(currentSession.user.id, currentSession);
          fetchUserRole(currentSession.user.id, currentSession);
        }, 0);
      }
      
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signOut = async () => {
    // Sign out and clear all auth state
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    setUserRole(null);
    setUserProfile(null);
  };

  const value = {
    user,
    session,
    loading,
    userRole,
    isAdmin: userRole === 'admin',
    userProfile,
    signOut,
    refreshUserRole,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
